import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  Building2, 
  Stethoscope, 
  ShoppingBag, 
  Home, 
  Leaf, 
  HardHat, 
  Clock, 
  Sparkles,
  Shield,
  CheckCircle2,
  Star,
  ChevronDown,
  ChevronUp,
  Send,
  Award,
  Users,
  BadgeCheck,
  ExternalLink,
  Lock,
  LogOut,
  Trash2,
  Eye,
  RefreshCw,
  Calendar,
  Filter
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company info
const COMPANY = {
  name: "Glasgow A-List Clean",
  phone: "07932 431 801",
  phoneFormatted: "+447932431801",
  email: "support@galcleaners.co.uk",
  address: "Unit B, 50 Stanley Street, Glasgow, G41 1JB",
  registeredAddress: "2/1 46 Fyvie Avenue, Glasgow, G43 1HA, Scotland",
  googleBusinessUrl: "https://www.google.com/search?q=Glasgow+A-List+Cleaners"
};

// Services data
const SERVICES = [
  {
    id: "office",
    title: "Office Cleaning",
    description: "Daily, weekly, or monthly office cleaning contracts tailored to your business needs. Desks, floors, kitchens, and common areas.",
    icon: Building2,
    large: true
  },
  {
    id: "medical",
    title: "Medical & Healthcare Cleaning",
    description: "Sterile cleaning for medical offices, dental practices, and healthcare facilities. Compliant with NHS standards.",
    icon: Stethoscope
  },
  {
    id: "retail",
    title: "Retail Cleaning",
    description: "Keep your shop floor spotless and welcoming for customers. Evening and overnight cleaning available.",
    icon: ShoppingBag
  },
  {
    id: "tenancy",
    title: "End of Tenancy",
    description: "Thorough deep cleaning for landlords and property managers. Get deposits back with our comprehensive service.",
    icon: Home
  },
  {
    id: "floor",
    title: "Floor Care",
    description: "Specialist floor cleaning, polishing, and maintenance for all surfaces including carpets, vinyl, and hardwood.",
    icon: Sparkles
  },
  {
    id: "dayporter",
    title: "Day Porter Services",
    description: "On-site cleaning staff throughout the day to maintain cleanliness and handle immediate needs.",
    icon: Clock
  },
  {
    id: "construction",
    title: "Post-Construction Cleaning",
    description: "Builders clean and sparkle cleans for newly built or renovated properties. Ready for handover.",
    icon: HardHat
  }
];

// Industries data
const INDUSTRIES = [
  "Corporate Offices",
  "Medical Practices",
  "Dental Clinics",
  "Retail Stores",
  "Restaurants & Cafes",
  "Schools & Nurseries",
  "Gyms & Fitness Centres",
  "Hotels & B&Bs",
  "Airbnb Properties",
  "Warehouses",
  "Showrooms",
  "Care Homes"
];

// Testimonials - Static reviews with Google Business link
const TESTIMONIALS = [
  {
    name: "Sarah Mitchell",
    role: "Office Manager, Anderson & Co Solicitors",
    text: "Glasgow A-List Clean has transformed our office. The team is reliable, thorough, and always goes the extra mile. Highly recommend for any business.",
    rating: 5,
    date: "November 2024"
  },
  {
    name: "Dr. James Campbell",
    role: "Practice Manager, Southside Medical Centre",
    text: "We've used several cleaning companies over the years, but none compare to A-List. Their attention to hygiene standards is exceptional.",
    rating: 5,
    date: "October 2024"
  },
  {
    name: "Karen Thomson",
    role: "Property Manager, Glasgow Lettings",
    text: "Their end of tenancy cleans are second to none. Our tenants always get their deposits back and new tenants walk into spotless properties.",
    rating: 5,
    date: "September 2024"
  }
];

// FAQ data
const FAQS = [
  {
    question: "What areas of Glasgow do you cover?",
    answer: "We serve all of Glasgow and surrounding areas including East Kilbride, Paisley, Hamilton, Motherwell, Cumbernauld, and Airdrie. Contact us if you're unsure about your location."
  },
  {
    question: "Do you provide all cleaning supplies and equipment?",
    answer: "Yes, we bring all necessary cleaning supplies, equipment, and materials. We use professional-grade, eco-friendly products unless you have specific preferences."
  },
  {
    question: "Are your cleaners insured and vetted?",
    answer: "Absolutely. All our cleaning staff are fully insured, background-checked, and receive ongoing training. We're also fully bonded for your peace of mind."
  },
  {
    question: "Can I get a recurring cleaning contract?",
    answer: "Yes! We offer daily, weekly, fortnightly, and monthly cleaning contracts. Long-term contracts receive preferential rates and priority scheduling."
  },
  {
    question: "What's included in your free walkthrough?",
    answer: "We visit your premises, assess your cleaning needs, discuss your requirements and schedule, and provide a detailed no-obligation quote within 24 hours."
  },
  {
    question: "How do I cancel or reschedule a cleaning?",
    answer: "We ask for 24 hours notice for any cancellations or rescheduling. Contact us by phone or email and we'll accommodate your needs."
  }
];

// Navigation links
const NAV_LINKS = [
  { href: "#services", label: "Services" },
  { href: "#industries", label: "Industries" },
  { href: "#green", label: "Green Cleaning" },
  { href: "#reviews", label: "Reviews" },
  { href: "#faq", label: "FAQ" },
  { href: "#contact", label: "Contact" }
];

// Service type mapping
const SERVICE_TYPES = {
  "office": "Office Cleaning",
  "medical": "Medical/Healthcare Cleaning",
  "retail": "Retail Cleaning",
  "tenancy": "End of Tenancy",
  "floor": "Floor Care",
  "dayporter": "Day Porter Services",
  "construction": "Post-Construction Cleaning",
  "other": "Other"
};

// Status badge colors
const STATUS_COLORS = {
  "new": "bg-blue-100 text-blue-800",
  "contacted": "bg-yellow-100 text-yellow-800",
  "quoted": "bg-purple-100 text-purple-800",
  "won": "bg-green-100 text-green-800",
  "lost": "bg-red-100 text-red-800"
};

// Navbar Component
const Navbar = ({ isAdmin = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isAdmin) {
    return (
      <nav className="navbar shadow-sm" data-testid="admin-navbar">
        <div className="container">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#0EA5E9] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg text-[#1C1C1C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Admin Dashboard
              </span>
            </a>
            <a href="/" className="text-slate-600 hover:text-[#0EA5E9] font-medium flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              View Site
            </a>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className={`navbar ${scrolled ? 'shadow-sm' : ''}`} data-testid="navbar">
      <div className="container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2" data-testid="logo">
            <div className="w-10 h-10 rounded-full bg-[#0EA5E9] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-[#1C1C1C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Glasgow A-List Clean
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-slate-600 hover:text-[#0EA5E9] font-medium transition-colors"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${COMPANY.phoneFormatted}`}
              className="btn-primary"
              data-testid="nav-call-btn"
            >
              <Phone className="w-5 h-5" />
              {COMPANY.phone}
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <>
          <div className="mobile-menu-overlay" onClick={() => setIsOpen(false)} />
          <div className="mobile-menu open" data-testid="mobile-menu">
            <div className="flex flex-col gap-6">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-lg font-medium text-[#1C1C1C] hover:text-[#0EA5E9] transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <hr className="border-slate-200" />
              <a
                href={`tel:${COMPANY.phoneFormatted}`}
                className="btn-primary w-full justify-center"
                data-testid="mobile-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="hero-section bg-gradient-to-br from-white via-sky-50/30 to-white" data-testid="hero-section">
      <div className="container relative z-10">
        <div className="lg:max-w-[45%] py-8 lg:py-0">
          {/* Trust badges */}
          <div className="flex flex-wrap gap-3 mb-6">
            <span className="trust-badge" data-testid="trust-badge-insured">
              <Shield className="w-4 h-4" />
              Fully Insured
            </span>
            <span className="trust-badge" data-testid="trust-badge-bonded">
              <BadgeCheck className="w-4 h-4" />
              Bonded
            </span>
          </div>

          {/* Headline */}
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-none text-[#1C1C1C] mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="hero-headline"
          >
            Glasgow Commercial Cleaning — Office, Airbnb, Retail Cleaning on Your Schedule
          </h1>

          {/* Subheadline */}
          <p className="text-lg lg:text-xl text-slate-600 leading-relaxed mb-8" data-testid="hero-subheadline">
            Reliable janitorial services for Glasgow businesses. Medical office cleaning, office cleaning contracts, and more. Free walkthrough and quote.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={`tel:${COMPANY.phoneFormatted}`}
              className="btn-primary w-full sm:w-auto justify-center"
              data-testid="hero-call-btn"
            >
              <Phone className="w-5 h-5" />
              Call {COMPANY.phone}
            </a>
            <a
              href="#contact"
              className="btn-secondary w-full sm:w-auto justify-center"
              data-testid="hero-quote-btn"
            >
              Get Free Quote
            </a>
          </div>

          {/* Social proof */}
          <div className="mt-8 flex items-center gap-4">
            <div className="flex -space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="w-10 h-10 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center">
                  <Users className="w-5 h-5 text-slate-500" />
                </div>
              ))}
            </div>
            <div>
              <div className="flex items-center gap-1 text-amber-500">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <p className="text-sm text-slate-600">Trusted by 100+ Glasgow businesses</p>
            </div>
          </div>
        </div>
      </div>

      {/* Hero image - visible on desktop */}
      <div className="hidden lg:block absolute right-0 top-0 w-[55%] h-full">
        <img
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80"
          alt="Professional commercial cleaning services in Glasgow office"
          className="w-full h-full object-cover"
          style={{ borderBottomLeftRadius: '4rem' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-transparent to-transparent" />
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" className="section bg-[#F8FAFC]" data-testid="services-section">
      <div className="container">
        <div className="section-header">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1C1C1C] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Commercial Cleaning Services in Glasgow
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            From daily office maintenance to specialist deep cleans, we deliver consistent, professional results.
          </p>
        </div>

        <div className="bento-grid">
          {SERVICES.map((service) => (
            <div
              key={service.id}
              className={`service-card ${service.large ? 'bento-item-large' : ''}`}
              data-testid={`service-${service.id}`}
            >
              <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center mb-4">
                <service.icon className="w-6 h-6 text-[#0EA5E9]" />
              </div>
              <h3 
                className="text-xl lg:text-2xl font-medium text-[#1C1C1C] mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {service.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Industries Section
const IndustriesSection = () => {
  return (
    <section id="industries" className="section" data-testid="industries-section">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1C1C1C] mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Industries We Serve Across Glasgow
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Whether you run a medical practice, manage retail spaces, or oversee corporate offices, we have the expertise and equipment to meet your specific cleaning requirements.
            </p>
            
            <div className="flex flex-wrap gap-3">
              {INDUSTRIES.map((industry) => (
                <span key={industry} className="industry-badge" data-testid={`industry-${industry.toLowerCase().replace(/\s+/g, '-')}`}>
                  <CheckCircle2 className="w-4 h-4 text-[#0EA5E9]" />
                  {industry}
                </span>
              ))}
            </div>
          </div>

          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=800&q=80"
              alt="Professional janitorial services for Glasgow retail and commercial spaces"
              className="w-full rounded-2xl shadow-lg"
            />
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg hidden md:block">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Award className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-[#1C1C1C]">100% Satisfaction</p>
                  <p className="text-sm text-slate-600">Guaranteed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Green Cleaning Section
const GreenCleaningSection = () => {
  return (
    <section id="green" className="section bg-gradient-to-br from-green-50 to-emerald-50/50" data-testid="green-section">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <img
              src="https://images.unsplash.com/photo-1714740568168-4c733e190045?auto=format&fit=crop&w=800&q=80"
              alt="Eco-friendly green cleaning solutions in Glasgow"
              className="w-full rounded-2xl shadow-lg"
            />
          </div>

          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Leaf className="w-4 h-4" />
              Eco-Friendly Option
            </div>

            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1C1C1C] mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Green Cleaning for Sustainable Glasgow Businesses
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Request our eco-friendly cleaning option and we'll use only environmentally safe, non-toxic products that are gentle on the planet without compromising on cleanliness.
            </p>

            <ul className="space-y-4 mb-8">
              {[
                "100% biodegradable cleaning products",
                "Reduced water usage techniques",
                "Microfibre technology to minimise chemical use",
                "Recyclable packaging and waste reduction"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>

            <a href="#contact" className="btn-primary inline-flex" data-testid="green-cta">
              Request Green Cleaning Quote
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Reviews Section - Updated with Google Business link
const ReviewsSection = () => {
  return (
    <section id="reviews" className="section" data-testid="reviews-section">
      <div className="container">
        <div className="section-header">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1C1C1C] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            What Glasgow Businesses Say About Us
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Don't just take our word for it. Here's what our clients have to say.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {TESTIMONIALS.map((testimonial, i) => (
            <div key={i} className="testimonial-card" data-testid={`testimonial-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="star-rating">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-current" />
                  ))}
                </div>
                <span className="text-sm text-slate-500">{testimonial.date}</span>
              </div>
              <p className="text-slate-700 mb-6 leading-relaxed">"{testimonial.text}"</p>
              <div>
                <p className="font-semibold text-[#1C1C1C]">{testimonial.name}</p>
                <p className="text-sm text-slate-500">{testimonial.role}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Google Reviews Link */}
        <div className="text-center">
          <a
            href={COMPANY.googleBusinessUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors"
            data-testid="google-reviews-link"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            See more reviews on Google
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Service Area Section
const ServiceAreaSection = () => {
  return (
    <section className="section bg-[#1C1C1C] text-white" data-testid="service-area-section">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Serving Glasgow & Surrounding Areas
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-6">
              Based in Glasgow's Southside, we provide commercial cleaning services throughout Greater Glasgow and the Central Belt.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                "Glasgow City Centre",
                "East Kilbride",
                "Paisley",
                "Hamilton",
                "Motherwell",
                "Cumbernauld",
                "Airdrie",
                "Coatbridge"
              ].map((area) => (
                <div key={area} className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#0EA5E9]" />
                  <span className="text-slate-300">{area}</span>
                </div>
              ))}
            </div>

            <a href="#contact" className="btn-primary inline-flex" data-testid="service-area-cta">
              Check Your Area
            </a>
          </div>

          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=800&q=80"
              alt="Glasgow cityscape - commercial cleaning service area"
              className="w-full rounded-2xl"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section id="faq" className="section" data-testid="faq-section">
      <div className="container max-w-3xl">
        <div className="section-header">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1C1C1C] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Everything you need to know about our commercial cleaning services.
          </p>
        </div>

        <div className="space-y-0">
          {FAQS.map((faq, i) => (
            <div key={i} className="faq-item" data-testid={`faq-${i}`}>
              <button
                className="faq-question"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                aria-expanded={openIndex === i}
              >
                {faq.question}
                {openIndex === i ? (
                  <ChevronUp className="w-5 h-5 text-[#0EA5E9]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              {openIndex === i && (
                <div className="faq-answer">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Form Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    postcode: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitResult(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSubmitResult({ success: true, message: response.data.message });
      setFormData({ name: '', email: '', phone: '', service_type: '', postcode: '', message: '' });
    } catch (error) {
      setSubmitResult({ 
        success: false, 
        message: error.response?.data?.detail || 'Something went wrong. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section bg-[#F8FAFC]" data-testid="contact-section">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1C1C1C] mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Get Your Free Walkthrough & Quote
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Book a free, no-obligation walkthrough of your premises. We'll assess your needs and provide a detailed quote within 24 hours.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#0EA5E9]" />
                </div>
                <div>
                  <p className="font-semibold text-[#1C1C1C]">Call Us</p>
                  <a href={`tel:${COMPANY.phoneFormatted}`} className="text-[#0EA5E9] hover:underline text-lg">
                    {COMPANY.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#0EA5E9]" />
                </div>
                <div>
                  <p className="font-semibold text-[#1C1C1C]">Email Us</p>
                  <a href={`mailto:${COMPANY.email}`} className="text-[#0EA5E9] hover:underline">
                    {COMPANY.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#0EA5E9]" />
                </div>
                <div>
                  <p className="font-semibold text-[#1C1C1C]">Visit Us</p>
                  <p className="text-slate-600">{COMPANY.address}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-lg">
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="John Smith"
                    data-testid="form-name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="john@company.co.uk"
                    data-testid="form-email"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="07xxx xxxxxx"
                    data-testid="form-phone"
                  />
                </div>
                <div>
                  <label htmlFor="postcode" className="block text-sm font-medium text-slate-700 mb-2">
                    Postcode
                  </label>
                  <input
                    type="text"
                    id="postcode"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="G41 1JB"
                    data-testid="form-postcode"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="service_type" className="block text-sm font-medium text-slate-700 mb-2">
                  Service Interested In
                </label>
                <select
                  id="service_type"
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="form-service"
                >
                  <option value="">Select a service...</option>
                  <option value="office">Office Cleaning</option>
                  <option value="medical">Medical/Healthcare Cleaning</option>
                  <option value="retail">Retail Cleaning</option>
                  <option value="tenancy">End of Tenancy</option>
                  <option value="floor">Floor Care</option>
                  <option value="dayporter">Day Porter Services</option>
                  <option value="construction">Post-Construction Cleaning</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                  Tell Us About Your Needs *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="form-input form-textarea"
                  placeholder="Please describe your premises and cleaning requirements..."
                  rows={4}
                  data-testid="form-message"
                />
              </div>

              {submitResult && (
                <div 
                  className={`p-4 rounded-lg mb-4 ${submitResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}
                  data-testid="form-result"
                >
                  {submitResult.message}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full justify-center"
                data-testid="form-submit"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Request Free Quote
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="footer" data-testid="footer">
      <div className="container">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#0EA5E9] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Glasgow A-List Clean
              </span>
            </div>
            <p className="text-slate-400 leading-relaxed">
              Professional commercial cleaning services for Glasgow businesses. Reliable, thorough, and always on schedule.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Quick Links
            </h3>
            <ul className="space-y-2">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="text-slate-400 hover:text-[#0EA5E9] transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Contact Information
            </h3>
            {/* NAP for SEO */}
            <address className="not-italic text-slate-400 space-y-2" itemScope itemType="https://schema.org/LocalBusiness">
              <p itemProp="name" className="sr-only">Glasgow A-List Clean</p>
              <p className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#0EA5E9]" />
                <a href={`tel:${COMPANY.phoneFormatted}`} itemProp="telephone" className="hover:text-[#0EA5E9]">
                  {COMPANY.phone}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#0EA5E9]" />
                <a href={`mailto:${COMPANY.email}`} itemProp="email" className="hover:text-[#0EA5E9]">
                  {COMPANY.email}
                </a>
              </p>
              <p className="flex items-start gap-2" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                <MapPin className="w-4 h-4 text-[#0EA5E9] flex-shrink-0 mt-1" />
                <span>
                  <span itemProp="streetAddress">Unit B, 50 Stanley Street</span>,{' '}
                  <span itemProp="addressLocality">Glasgow</span>,{' '}
                  <span itemProp="postalCode">G41 1JB</span>
                </span>
              </p>
            </address>
          </div>
        </div>

        <hr className="border-slate-800 mb-6" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500">
          <p>&copy; {new Date().getFullYear()} Glasgow A-List Clean Ltd. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <a href="/admin" className="hover:text-[#0EA5E9] transition-colors">Admin</a>
            <span>Company Number: SC863125</span>
          </div>
        </div>
      </div>

      {/* Schema.org LocalBusiness Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://galcleaners.co.uk",
          "name": "Glasgow A-List Clean",
          "image": "https://galcleaners.co.uk/logo.png",
          "description": "Professional commercial cleaning services in Glasgow. Office cleaning, medical facility cleaning, retail cleaning, and janitorial services.",
          "telephone": "+447932431801",
          "email": "support@galcleaners.co.uk",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Unit B, 50 Stanley Street",
            "addressLocality": "Glasgow",
            "postalCode": "G41 1JB",
            "addressCountry": "GB"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "55.8445",
            "longitude": "-4.2688"
          },
          "url": "https://galcleaners.co.uk",
          "priceRange": "$$",
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "08:00",
            "closes": "18:00"
          },
          "areaServed": {
            "@type": "GeoCircle",
            "geoMidpoint": {
              "@type": "GeoCoordinates",
              "latitude": "55.8642",
              "longitude": "-4.2518"
            },
            "geoRadius": "30000"
          },
          "sameAs": [],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Commercial Cleaning Services",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Office Cleaning Glasgow"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Medical Office Cleaning"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Janitorial Services Glasgow"
                }
              }
            ]
          }
        })
      }} />
    </footer>
  );
};

// Floating Call Button for Mobile
const FloatingCallButton = () => {
  return (
    <a
      href={`tel:${COMPANY.phoneFormatted}`}
      className="floating-cta"
      aria-label="Call Glasgow A-List Clean"
      data-testid="floating-call-btn"
    >
      <Phone className="w-6 h-6" />
    </a>
  );
};

// Admin Dashboard Component
const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        localStorage.setItem('adminPassword', password);
        fetchContacts(password);
      }
    } catch (err) {
      setError('Invalid password');
    }
  };

  const fetchContacts = async (pwd) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/contacts?password=${pwd || password}`);
      setContacts(response.data);
    } catch (err) {
      setError('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (contactId, newStatus) => {
    try {
      await axios.put(`${API}/admin/contacts/${contactId}/status?password=${password}`, { status: newStatus });
      fetchContacts();
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${contactId}?password=${password}`);
      fetchContacts();
      setSelectedContact(null);
    } catch (err) {
      setError('Failed to delete contact');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    localStorage.removeItem('adminPassword');
  };

  // Check for stored password on mount
  useEffect(() => {
    const storedPassword = localStorage.getItem('adminPassword');
    if (storedPassword) {
      setPassword(storedPassword);
      setIsAuthenticated(true);
      fetchContacts(storedPassword);
    }
  }, []);

  const filteredContacts = statusFilter === 'all' 
    ? contacts 
    : contacts.filter(c => c.status === statusFilter);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-[#0EA5E9] flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Admin Login
            </h1>
            <p className="text-slate-600 mt-2">Enter your password to access the dashboard</p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                placeholder="Enter admin password"
                data-testid="admin-password"
              />
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-50 text-red-800 mb-4 text-sm">
                {error}
              </div>
            )}

            <button type="submit" className="btn-primary w-full justify-center" data-testid="admin-login-btn">
              <Lock className="w-5 h-5" />
              Login
            </button>
          </form>

          <p className="text-center mt-6 text-sm text-slate-500">
            <a href="/" className="text-[#0EA5E9] hover:underline">← Back to website</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar isAdmin={true} />
      
      <div className="container py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#1C1C1C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Quote Requests
            </h1>
            <p className="text-slate-600 mt-1">{contacts.length} total enquiries</p>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => fetchContacts()} 
              className="btn-secondary py-2 px-4"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button 
              onClick={handleLogout}
              className="flex items-center gap-2 text-slate-600 hover:text-red-600 font-medium transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 mb-6 flex flex-wrap items-center gap-3">
          <Filter className="w-5 h-5 text-slate-400" />
          <span className="text-sm font-medium text-slate-600">Filter by status:</span>
          {['all', 'new', 'contacted', 'quoted', 'won', 'lost'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                statusFilter === status 
                  ? 'bg-[#0EA5E9] text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`filter-${status}`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-[#0EA5E9] animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Loading enquiries...</p>
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No enquiries found</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Contacts List */}
            <div className="lg:col-span-2 space-y-4">
              {filteredContacts.map((contact) => (
                <div
                  key={contact.id}
                  className={`bg-white rounded-xl p-4 cursor-pointer transition-all border-2 ${
                    selectedContact?.id === contact.id 
                      ? 'border-[#0EA5E9] shadow-lg' 
                      : 'border-transparent hover:shadow-md'
                  }`}
                  onClick={() => setSelectedContact(contact)}
                  data-testid={`contact-${contact.id}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-[#1C1C1C]">{contact.name}</h3>
                      <p className="text-sm text-slate-500">
                        {SERVICE_TYPES[contact.service_type] || contact.service_type || 'General Enquiry'}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[contact.status] || 'bg-slate-100 text-slate-600'}`}>
                      {contact.status}
                    </span>
                  </div>
                  <p className="text-slate-600 text-sm line-clamp-2 mb-3">{contact.message}</p>
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(contact.timestamp).toLocaleDateString('en-GB')}
                    </span>
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {contact.phone}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Detail */}
            <div className="lg:col-span-1">
              {selectedContact ? (
                <div className="bg-white rounded-xl p-6 sticky top-24">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-bold text-xl text-[#1C1C1C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {selectedContact.name}
                    </h3>
                    <button
                      onClick={() => deleteContact(selectedContact.id)}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                      data-testid="delete-contact-btn"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Email</p>
                      <a href={`mailto:${selectedContact.email}`} className="text-[#0EA5E9] hover:underline">
                        {selectedContact.email}
                      </a>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Phone</p>
                      <a href={`tel:${selectedContact.phone}`} className="text-[#0EA5E9] hover:underline">
                        {selectedContact.phone}
                      </a>
                    </div>
                    {selectedContact.postcode && (
                      <div>
                        <p className="text-sm font-medium text-slate-500 mb-1">Postcode</p>
                        <p className="text-slate-700">{selectedContact.postcode}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Service</p>
                      <p className="text-slate-700">
                        {SERVICE_TYPES[selectedContact.service_type] || selectedContact.service_type || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Message</p>
                      <p className="text-slate-700">{selectedContact.message}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500 mb-1">Submitted</p>
                      <p className="text-slate-700">
                        {new Date(selectedContact.timestamp).toLocaleString('en-GB')}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-2">Update Status</p>
                    <div className="flex flex-wrap gap-2">
                      {['new', 'contacted', 'quoted', 'won', 'lost'].map((status) => (
                        <button
                          key={status}
                          onClick={() => updateStatus(selectedContact.id, status)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                            selectedContact.status === status
                              ? STATUS_COLORS[status]
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                          data-testid={`status-${status}`}
                        >
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <a
                      href={`tel:${selectedContact.phone}`}
                      className="btn-primary w-full justify-center"
                      data-testid="call-contact-btn"
                    >
                      <Phone className="w-5 h-5" />
                      Call Now
                    </a>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl p-6 text-center">
                  <Eye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Select an enquiry to view details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <>
      <Navbar />
      <main>
        <HeroSection />
        <ServicesSection />
        <IndustriesSection />
        <GreenCleaningSection />
        <ReviewsSection />
        <ServiceAreaSection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
      <FloatingCallButton />
    </>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
